/* React */
import { useEffect, useRef } from 'react';

/* types */
import { CustomTableView, TableViewState, View } from '../types';

/**
 * A custom hook that updates the table state based on URL parameters.
 *
 * @param {Object} props - The props object.
 * @param {boolean} [props.ignoreSearchParams] - Whether to ignore URL parameters.
 * @param {Function} props.setCurrentTableView - Function to update the current table view.
 * @param {TableViewState} props.stateFromURL - Table state derived from URL parameters.
 * @param {CustomTableView | View} props.tableView - Current table view configuration.
 * @param {Function} props.setViewHasBeenModified - Function to set whether view has been modified.
 * @param {string} props.tableName - Name of the table.
 * @returns {void}
 */
export const useUpdateCurrentView = ({
    ignoreSearchParams,
    setCurrentTableView,
    stateFromURL,
    tableView
}: {
    ignoreSearchParams?: boolean;
    setCurrentTableView: (view: CustomTableView | View) => void;
    setViewHasBeenModified: (value: boolean) => void;
    stateFromURL: TableViewState;
    tableName: string;
    tableView: CustomTableView | View;
}): void => {
    const { columnVisibility, columnOrder, columnFilters, sorting } =
        stateFromURL;

    const isCalled = useRef(false);

    useEffect(() => {
        if (
            tableView &&
            !ignoreSearchParams &&
            !isCalled.current &&
            Object.keys(stateFromURL).length > 0
        ) {
            const newTable = {
                ...tableView,
                state: {
                    ...tableView.state,
                    columnFilters,
                    columnOrder,
                    columnVisibility,
                    sorting
                }
            };
            setCurrentTableView(newTable);
            isCalled.current = true;
        }
    }, [
        columnFilters,
        columnOrder,
        columnVisibility,
        ignoreSearchParams,
        setCurrentTableView,
        sorting,
        stateFromURL,
        tableView
    ]);
};
